import { z } from 'zod';

export type ChartType = 'bar' | 'line' | 'pie' | 'doughnut' | 'area' | 'scatter'

// New interface for theme-aware colors
export interface ThemeAwareColor {
  light: string;  // Color for light backgrounds
  dark: string;   // Color for dark backgrounds
}

// Color can be either a simple string or theme-aware colors
export type ChartColor = string | ThemeAwareColor;

export interface ChartStudy {
  id: string
  type: 'regression' | 'movingAverage' | 'polynomialTrend' | 'exponentialSmoothing' | 'mean' | 'median' | 'mode' | 'stdDeviation'
  name: string
  period?: number
  degree?: number
  alpha?: number
  color: ChartColor  // Updated to support theme-aware colors
  visible: boolean
}

export interface ChartDataPoint {
  label: string
  value: number
  color?: string
}

// Universal data point for simplified chart interface
export interface UniversalDataPoint {
  [key: string]: string | number | Date
}

// Universal Chart Interface - Single source of truth for all chart operations
export interface UniversalChart {
  // Core Identity
  id?: string
  title: string
  description?: string
  
  // Data Structure - simplified to key-value pairs
  data: UniversalDataPoint[]
  chartType: ChartType
  xAxis: string
  yAxis: string
  
  // Visual Configuration
  configuration?: ChartConfiguration
  colors?: string[]
  
  // Advanced Features (ALWAYS available)
  studies?: ChartStudy[]
  axisOptions?: AxisOptions
  displayOptions?: ChartDisplayOptions
  
  // Metadata
  metadata?: {
    yAxisType?: 'number' | 'currency' | 'percent'
    dataSource?: string
    queryType?: string
    dateRange?: string
    recordCount?: number
    originalXAxis?: string
    originalYAxis?: string
    isMultiSeries?: boolean
  }
  
  // Database Fields (optional)
  organizationId?: string
  userId?: string
  tags?: string[]
  isPublic?: boolean
  createdAt?: Date
  updatedAt?: Date
  lastViewedAt?: Date
  viewCount?: number
  savedToDatabase?: boolean
}

export interface ChartDataset {
  label: string
  data: ChartDataPoint[]
  backgroundColor?: string[]
  borderColor?: string[]
  borderWidth?: number
}

export interface ChartConfiguration {
  responsive?: boolean
  maintainAspectRatio?: boolean
  plugins?: {
    legend?: {
      display?: boolean
      position?: 'top' | 'bottom' | 'left' | 'right'
    }
    title?: {
      display?: boolean
      text?: string
    }
  }
  scales?: {
    x?: {
      display?: boolean
      title?: {
        display?: boolean
        text?: string
      }
    }
    y?: {
      display?: boolean
      title?: {
        display?: boolean
        text?: string
      }
    }
  }
  // Add theme-aware single color support
  singleColor?: ChartColor;
  colorPalette?: string;
}

// Zod schema for AxisOptions
export const AxisOptions = z.object({
  xMin: z.union([z.number(), z.string()]).nullable().optional(),
  xMax: z.union([z.number(), z.string()]).nullable().optional(),
  yMin: z.number().nullable().optional(),
  yMax: z.number().nullable().optional(),
});
export type AxisOptions = z.infer<typeof AxisOptions>;

// Zod schema for ChartDisplayOptions
export const ChartDisplayOptions = z.object({
  showGridLines: z.boolean().optional(),
  showLegend: z.boolean().optional(),
});
export type ChartDisplayOptions = z.infer<typeof ChartDisplayOptions>;

export const ChartSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  userId: z.string(),
  title: z.string(),
  description: z.string().optional(),
  type: z.enum(['bar', 'line', 'pie', 'doughnut', 'area', 'scatter']),
  datasets: z.array(z.object({
    label: z.string(),
    data: z.array(z.object({
      label: z.string(),
      value: z.number(),
      color: z.string().optional(),
    })),
    backgroundColor: z.array(z.string()).optional(),
    borderColor: z.array(z.string()).optional(),
    borderWidth: z.number().optional(),
  })),
  configuration: z.object({
    responsive: z.boolean().optional(),
    maintainAspectRatio: z.boolean().optional(),
    plugins: z.object({
      legend: z.object({
        display: z.boolean().optional(),
        position: z.enum(['top', 'bottom', 'left', 'right']).optional(),
      }).optional(),
      title: z.object({
        display: z.boolean().optional(),
        text: z.string().optional(),
      }).optional(),
    }).optional(),
    scales: z.object({
      x: z.object({
        display: z.boolean().optional(),
        title: z.object({
          display: z.boolean().optional(),
          text: z.string().optional(),
        }).optional(),
      }).optional(),
      y: z.object({
        display: z.boolean().optional(),
        title: z.object({
          display: z.boolean().optional(),
          text: z.string().optional(),
        }).optional(),
      }).optional(),
    }).optional(),
    singleColor: z.union([z.string(), z.object({
      light: z.string(),
      dark: z.string(),
    })]).optional(),
    colorPalette: z.string().optional(),
  }),
  tags: z.array(z.string()).optional(),
  isPublic: z.boolean(),
  createdAt: z.date(),
  updatedAt: z.date(),
  lastViewedAt: z.date().optional(),
  viewCount: z.number(),
  studies: z.array(z.object({
    id: z.string(),
    type: z.enum(['regression', 'movingAverage', 'polynomialTrend', 'exponentialSmoothing', 'mean', 'median', 'mode', 'stdDeviation']),
    name: z.string(),
    period: z.number().optional(),
    degree: z.number().optional(),
    alpha: z.number().optional(),
    color: z.union([z.string(), z.object({
      light: z.string(),
      dark: z.string(),
    })]),
    visible: z.boolean(),
  })).optional(),
  axisOptions: AxisOptions.optional(),
  displayOptions: ChartDisplayOptions.optional(),
  metadata: z.object({
    yAxisType: z.enum(['number', 'currency', 'percent']).optional(),
    originalXAxis: z.string().optional(),
    originalYAxis: z.string().optional(),
    isMultiSeries: z.boolean().optional(),
  }).optional(),
});

// Create the TypeScript type from the schema
export type Chart = z.infer<typeof ChartSchema>;

export interface CreateChart {
  organizationId: string
  userId: string
  title: string
  description?: string
  type: ChartType
  datasets: ChartDataset[]
  configuration?: Partial<ChartConfiguration>
  tags?: string[]
  isPublic?: boolean
  axisOptions?: AxisOptions;
  displayOptions?: ChartDisplayOptions;
}

export interface UpdateChart {
  title?: string
  description?: string
  type?: ChartType
  datasets?: ChartDataset[]
  configuration?: Partial<ChartConfiguration>
  tags?: string[]
  isPublic?: boolean
  studies?: ChartStudy[]
  axisOptions?: AxisOptions;
  displayOptions?: ChartDisplayOptions;
}

// Helper functions for working with theme-aware colors
export const isThemeAwareColor = (color: ChartColor): color is ThemeAwareColor => {
  return typeof color === 'object' && color !== null && 'light' in color && 'dark' in color;
};

export const getColorForTheme = (color: ChartColor, isDark: boolean): string => {
  if (isThemeAwareColor(color)) {
    return isDark ? color.dark : color.light;
  }
  return color;
};

// Chart conversion utilities
export const convertDatabaseChartToUniversal = (dbChart: Chart): UniversalChart => {
  // Convert datasets to simple key-value data structure
  const data: UniversalDataPoint[] = [];
  let colors: string[] = [];
  
  // Try to extract original axis mapping from metadata or use sensible defaults
  let xAxisName = 'name';
  let yAxisName = 'value';
  
  // Check if metadata contains original axis information
  if (dbChart.metadata?.originalXAxis) {
    xAxisName = dbChart.metadata.originalXAxis;
  }
  if (dbChart.metadata?.originalYAxis) {
    yAxisName = dbChart.metadata.originalYAxis;
  }
  
  if (dbChart.datasets && dbChart.datasets.length > 0) {
    if (dbChart.datasets.length === 1) {
      // Single dataset - simple conversion
      const dataset = dbChart.datasets[0];
      dataset.data.forEach(point => {
        data.push({
          [xAxisName]: point.label,
          [yAxisName]: point.value
        });
      });
      
      // Extract colors from the dataset
      if (dataset.backgroundColor && Array.isArray(dataset.backgroundColor)) {
        colors = dataset.backgroundColor;
      }
    } else {
      // Multiple datasets - convert to multi-series format
      // Create a combined data structure where each point has multiple series values
      const combinedData: Record<string, UniversalDataPoint> = {};
      
      dbChart.datasets.forEach((dataset, datasetIndex) => {
        dataset.data.forEach(point => {
          const key = point.label; // This is the product name (Widget A, Widget B)
          if (!combinedData[key]) {
            combinedData[key] = {
              [xAxisName]: point.label // Use the actual product name, not a generic name
            };
          }
          
          // Add this dataset's value as a property (dataset.label is the month name)
          combinedData[key][dataset.label] = point.value;
        });
        
        // Collect colors from each dataset
        if (dataset.backgroundColor && Array.isArray(dataset.backgroundColor) && dataset.backgroundColor.length > 0) {
          colors.push(dataset.backgroundColor[0]);
        }
      });
      
      // Convert to array and ensure proper data structure
      data.push(...Object.values(combinedData));
      
      console.log('[convertDatabaseChartToUniversal] Multi-series conversion result:', {
        dataPointsCount: data.length,
        dataStructure: data.map(point => ({ 
          xValue: point[xAxisName], 
          keys: Object.keys(point).filter(k => k !== xAxisName) 
        })),
        colors: colors.length
      });
      
      // For multi-series, the yAxis should be the first dataset label (first month)
      if (dbChart.datasets[0]?.label) {
        yAxisName = dbChart.datasets[0].label;
      }
    }
  }
  
  return {
    id: dbChart.id,
    title: dbChart.title,
    description: dbChart.description,
    data,
    chartType: dbChart.type,
    xAxis: xAxisName,
    yAxis: yAxisName,
    configuration: dbChart.configuration,
    colors: colors.length > 0 ? colors : undefined,
    studies: dbChart.studies,
    axisOptions: dbChart.axisOptions,
    displayOptions: dbChart.displayOptions,
    metadata: dbChart.metadata,
    organizationId: dbChart.organizationId,
    userId: dbChart.userId,
    tags: dbChart.tags,
    isPublic: dbChart.isPublic,
    createdAt: dbChart.createdAt,
    updatedAt: dbChart.updatedAt,
    lastViewedAt: dbChart.lastViewedAt,
    viewCount: dbChart.viewCount,
    savedToDatabase: true
  };
};

export const convertUniversalToDatabase = (universalChart: UniversalChart): Partial<Chart> => {
  // Detect if this is multi-series data by checking if data points have multiple numeric values
  const firstDataPoint = universalChart.data[0];
  const isMultiSeries = firstDataPoint && Object.keys(firstDataPoint).filter(key => 
    key !== universalChart.xAxis && 
    typeof firstDataPoint[key] === 'number'
  ).length > 1;

  let datasets: ChartDataset[] = [];

  if (isMultiSeries && firstDataPoint) {
    // Multi-series: create one dataset per numeric column
    const seriesKeys = Object.keys(firstDataPoint).filter(key => 
      key !== universalChart.xAxis && 
      typeof firstDataPoint[key] === 'number'
    );
    
    datasets = seriesKeys.map((seriesKey, index) => ({
      label: seriesKey,
      data: universalChart.data.map(point => ({
        label: String(point[universalChart.xAxis] || ''),
        value: Number(point[seriesKey] || 0),
        color: undefined
      })),
      backgroundColor: universalChart.colors && universalChart.colors[index] ? [universalChart.colors[index]] : undefined,
      borderColor: universalChart.colors && universalChart.colors[index] ? [universalChart.colors[index]] : undefined,
      borderWidth: 1
    }));
  } else {
    // Single series: create one dataset with the yAxis values
    datasets = [{
      label: universalChart.title,
      data: universalChart.data.map(point => ({
        label: String(point[universalChart.xAxis] || ''),
        value: Number(point[universalChart.yAxis] || 0),
        color: undefined
      })),
      backgroundColor: universalChart.colors,
      borderColor: universalChart.colors,
      borderWidth: 1
    }];
  }
  
  // Preserve original axis information in metadata for round-trip conversion
  const enhancedMetadata = {
    ...universalChart.metadata,
    originalXAxis: universalChart.xAxis,
    originalYAxis: universalChart.yAxis,
    isMultiSeries
  };
  
  return {
    id: universalChart.id,
    title: universalChart.title,
    description: universalChart.description,
    type: universalChart.chartType,
    datasets,
    configuration: universalChart.configuration || {},
    studies: universalChart.studies,
    axisOptions: universalChart.axisOptions,
    displayOptions: universalChart.displayOptions,
    metadata: enhancedMetadata,
    organizationId: universalChart.organizationId || '',
    userId: universalChart.userId || '',
    tags: universalChart.tags,
    isPublic: universalChart.isPublic || false,
    createdAt: universalChart.createdAt || new Date(),
    updatedAt: universalChart.updatedAt || new Date(),
    lastViewedAt: universalChart.lastViewedAt,
    viewCount: universalChart.viewCount || 0
  };
}; 